import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';
import { getIntersectionRoot } from '@theme/scroll-container';

const MAX_PLANKEN = 10;
// Matches --animation-speed-slow (theme-styles-variables.liquid) — how long the alternatives
// panel's row-space takes to grow open before its content starts fading in.
const ALTERNATIVES_GROW_MS = 200;

/**
 * Interactive PDP bundle builder: lets a customer check/uncheck curated components, swap one
 * for a curated alternative, and adjust quantity per component and per "plank" (recipe yield),
 * then adds the real, currently selected products to the cart as separate line items — no
 * fixed bundle SKU. State lives entirely on each `.bundle-recipe-card`'s dataset (included,
 * quantity, variantId, price, vendor, title) rather than a parallel JS map, since the number of
 * cards is small and DOM-as-state keeps swap/checkbox/quantity interactions trivially in sync.
 *
 * The quantity/included bookkeeping on a card is written from exactly one place: the
 * `quantity-stepper:change` listener below, reacting to the shared
 * `<quantity-stepper-component>` (assets/component-quantity-stepper.js, `mode="toggle"`) each
 * card embeds. Nothing here pokes that component's internals directly — the checkbox calls its
 * `setActive()`, the "N planken" control calls its `setQuantity()`, and both changes flow back
 * through the same change event so card bookkeeping and the checkbox stay in sync regardless of
 * which one triggered the change (including the stepper's own trash-click, which sets active
 * false without going through the checkbox at all).
 *
 * Deliberately separate from box-builder.js / box-builder.liquid (different data model: curated
 * slots with fixed alternatives, not free product selection from a collection) — but shares the
 * quantity-stepper component with it.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} footerPrice
 * @property {HTMLElement} [footerOriginalPrice]
 * @property {HTMLElement} error
 * @property {HTMLElement} [plankValue]
 * @property {HTMLButtonElement} [plankMinus]
 * @property {HTMLButtonElement} [plankPlus]
 * @property {HTMLElement} [footer]
 * @property {HTMLElement} [endSentinel]
 *
 * @extends {Component<Refs>}
 */
class BundleRecipeBuilder extends Component {
  /** @type {number} */
  #plankQuantity = 1;

  /** @type {number | undefined} */
  #addedTimeout;

  /** @type {IntersectionObserver | null} */
  #footerStuckObserver = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.refs.plankMinus) this.refs.plankMinus.disabled = true;
    this.addEventListener('quantity-stepper:change', this.#handleStepperChange);
    this.#updateTotals();
    this.#setupFooterStuckObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('quantity-stepper:change', this.#handleStepperChange);
    clearTimeout(this.#addedTimeout);
    this.#footerStuckObserver?.disconnect();
  }

  /**
   * The footer is `position: sticky; bottom: 0`, so it only visually "sticks" (floats over the
   * product list, needs its divider) while there's still content below the viewport to scroll
   * through. Once the end of the component scrolls into view, the footer has settled back into
   * its normal inline position — no overlap, so no divider, and a bit more breathing room above
   * it instead. `endSentinel` is a 1px marker right after the last piece of content: as long as
   * it's on screen, the footer can't be stuck (same sentinel technique header.js uses for the
   * top-sticky header, mirrored for a bottom-sticky element instead).
   */
  #setupFooterStuckObserver() {
    const { footer, endSentinel } = this.refs;
    if (!footer || !endSentinel) return;

    this.#footerStuckObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        footer.dataset.stuck = entry.isIntersecting ? 'false' : 'true';
      },
      { root: getIntersectionRoot(), threshold: 0 }
    );
    this.#footerStuckObserver.observe(endSentinel);
  }

  increasePlanken() {
    this.#setPlankQuantity(this.#plankQuantity + 1);
  }

  decreasePlanken() {
    this.#setPlankQuantity(this.#plankQuantity - 1);
  }

  /**
   * The checkbox is a plain sibling element, not part of the shared stepper — it only calls
   * the stepper's public `setActive()`. The resulting `quantity-stepper:change` event (handled
   * below) is what actually updates the card's bookkeeping, so this stays the single write path
   * regardless of whether active/inactive was triggered by the checkbox or by the stepper's own
   * trash/plus click.
   * @param {Event} event
   */
  toggleIncluded(event) {
    const checkbox = event.target;
    if (!(checkbox instanceof HTMLInputElement)) return;

    const card = checkbox.closest('.bundle-recipe-card');
    if (!(card instanceof HTMLElement)) return;

    const stepper = this.#stepperFor(card);
    if (stepper && typeof (/** @type {any} */ (stepper).setActive) === 'function') {
      /** @type {any} */ (stepper).setActive(checkbox.checked);
    }
  }

  /**
   * `component-quantity-stepper.js` loads as its own `<script type="module">` alongside this
   * one, with no import relationship between them — there's no guarantee it's finished
   * upgrading the element by the time a user interacts with a checkbox right after the page
   * paints. `customElements.upgrade()` forces that synchronously if needed (matching the same
   * safety net assets/component.js's own click/change delegation already applies to itself);
   * without it, a fast first click on an unupgraded stepper would silently no-op.
   * @param {HTMLElement} card
   * @returns {Element | null}
   */
  #stepperFor(card) {
    const stepper = card.querySelector('quantity-stepper-component');
    if (stepper) customElements.upgrade(stepper);
    return stepper;
  }

  /** @param {CustomEvent<{quantity: number, active: boolean}>} event */
  #handleStepperChange = (event) => {
    if (!(event.target instanceof Element)) return;

    const card = event.target.closest('.bundle-recipe-card');
    if (!(card instanceof HTMLElement)) return;

    card.dataset.quantity = String(event.detail.quantity);
    card.dataset.included = String(event.detail.active);

    const checkbox = card.querySelector('.bundle-recipe-card__checkbox');
    if (checkbox instanceof HTMLInputElement) checkbox.checked = event.detail.active;

    this.#updateCardPriceDisplay(card);
    this.#updateTotals();
  };

  /**
   * Opening is choreographed in two steps so it doesn't feel abrupt: the panel's row-space
   * grows first (CSS grid-template-rows 0fr -> 1fr, see bundle-recipe-alternatives.liquid),
   * then — once that's had time to play out — the cards fade in via `.is-visible`. Closing
   * (here or via #chooseAlternative) is immediate in both steps; only opening is staggered.
   * @param {Event} event
   */
  toggleAlternatives(event) {
    if (!(event.target instanceof Element)) return;

    const card = event.target.closest('.bundle-recipe-card');
    if (!(card instanceof HTMLElement)) return;

    const panel = card.querySelector('.bundle-recipe-alternatives');
    if (!(panel instanceof HTMLElement)) return;

    const opening = panel.dataset.open !== 'true';

    // Only one alternatives panel open at a time.
    for (const otherPanel of this.querySelectorAll('.bundle-recipe-alternatives')) {
      if (otherPanel === panel) continue;
      this.#closeAlternatives(otherPanel);
    }

    if (opening) {
      this.#syncAlternativesVisibility(card);
      panel.dataset.open = 'true';
      const content = panel.querySelector('.bundle-recipe-alternatives__content');
      window.setTimeout(() => content?.classList.add('is-visible'), ALTERNATIVES_GROW_MS);
    } else {
      this.#closeAlternatives(panel);
    }
  }

  /** @param {Element} panel */
  #closeAlternatives(panel) {
    panel.dataset.open = 'false';
    panel.querySelector('.bundle-recipe-alternatives__content')?.classList.remove('is-visible');
  }

  /** @param {Event} event */
  chooseAlternative(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;

    const card = button.closest('.bundle-recipe-card');
    if (!(card instanceof HTMLElement)) return;

    const { variantId, price, title, vendor, url, weight, origin, image, inventoryMax } = button.dataset;
    if (!variantId || !price) return;

    card.dataset.variantId = variantId;
    card.dataset.price = price;
    card.dataset.vendor = vendor || '';
    card.dataset.title = title || '';

    const stepper = this.#stepperFor(card);
    if (stepper) {
      if (inventoryMax != null) {
        stepper.dataset.max = inventoryMax;
      } else {
        delete stepper.dataset.max;
      }
      // Re-clamps to the new max if the swapped-in variant has less stock than the current
      // quantity — setQuantity always applies data-max, so calling it with its own current
      // value is enough to reclamp without otherwise changing anything.
      if (typeof (/** @type {any} */ (stepper).setQuantity) === 'function') {
        /** @type {any} */ (stepper).setQuantity(this.#cardQuantity(card));
      }

      // Kiezen voor een alternatief is een duidelijke keuze-intentie: een nog niet aangevinkte
      // kaart mag er dan gerust ook meteen bij horen, i.p.v. dat je na het wisselen alsnog los
      // het vinkje moet zetten. Via setActive() i.p.v. card.dataset direct, zodat dit door
      // dezelfde quantity-stepper:change-flow loopt als de checkbox (zie class-doc hierboven) en
      // checkbox/bookkeeping gegarandeerd in sync blijven. Was de kaart al aangevinkt, dan blijft
      // die staat (en de huidige hoeveelheid) ongemoeid.
      if (card.dataset.included !== 'true' && typeof (/** @type {any} */ (stepper).setActive) === 'function') {
        /** @type {any} */ (stepper).setActive(true);
      }
    }

    const titleLink = card.querySelector('.bundle-recipe-card__title');
    if (titleLink instanceof HTMLAnchorElement) {
      const badge = titleLink.querySelector('.bio-badge');
      titleLink.replaceChildren(document.createTextNode(title || ''));
      if (badge) titleLink.append(badge);
      if (url) titleLink.href = url;
    }

    const mediaLink = card.querySelector('.bundle-recipe-card__media-link');
    if (mediaLink instanceof HTMLAnchorElement && url) mediaLink.href = url;

    if (image && mediaLink) {
      // The active variant when the card first rendered might have had no image at all, in
      // which case bundle-recipe-slot-card.liquid rendered a placeholder <svg class="…__image
      // …__placeholder placeholder"> in this spot instead of an <img> (same .bundle-recipe-
      // card__image class, for shared sizing CSS — see the stylesheet below). Swapping to an
      // alternative that *does* have a photo then needs a real <img>, not just new attributes
      // on an element that was never one.
      let mediaImg = mediaLink.querySelector('.bundle-recipe-card__image');
      if (!(mediaImg instanceof HTMLImageElement)) {
        const newImg = document.createElement('img');
        newImg.className = 'bundle-recipe-card__image';
        newImg.width = 160;
        newImg.height = 160;
        newImg.loading = 'lazy';
        mediaImg?.replaceWith(newImg);
        mediaImg = newImg;
      }

      // image_tag (bundle-recipe-slot-card.liquid) auto-generates a srcset, and once a srcset
      // is present browsers resolve the image from that, not from src — updating src alone left
      // the old photo showing (only the swapped-in variant's single fixed-size URL is available
      // here from data-image, no responsive set to swap srcset to, so drop it instead).
      mediaImg.alt = title || '';
      mediaImg.src = image;
      mediaImg.removeAttribute('srcset');
    }

    const metaEl = card.querySelector('.bundle-recipe-card__meta');
    if (metaEl instanceof HTMLElement) {
      const metaText = [weight, origin].filter(Boolean).join(' • ');
      metaEl.textContent = metaText;
      metaEl.hidden = metaText === '';
    }

    this.#updateCardPriceDisplay(card);

    const panel = card.querySelector('.bundle-recipe-alternatives');
    if (panel instanceof HTMLElement) {
      const panelTitle = panel.querySelector('.bundle-recipe-alternatives__title');
      if (panelTitle) panelTitle.textContent = `Alternatieven voor ${title || ''}`;

      this.#syncAlternativesVisibility(card);
      this.#closeAlternatives(panel);
    }

    this.#updateTotals();
  }

  /**
   * Hides whichever alternative item currently matches the card's active variant — the panel
   * always server-renders every curated alternative (see bundle-recipe-alternatives.liquid), so
   * swapping back and forth stays possible; only the one that's already showing on the card gets
   * hidden from its own panel.
   * @param {HTMLElement} card
   */
  #syncAlternativesVisibility(card) {
    const panel = card.querySelector('.bundle-recipe-alternatives');
    if (!panel) return;

    for (const item of panel.querySelectorAll('.bundle-recipe-alternatives__item')) {
      const chooseBtn = item.querySelector('.bundle-recipe-alternatives__choose');
      const isActive = chooseBtn instanceof HTMLElement && chooseBtn.dataset.variantId === card.dataset.variantId;
      if (item instanceof HTMLElement) item.hidden = isActive;
    }
  }

  /**
   * Shows quantity × unit price (a line total), not just the unit price — kept separate from
   * card.dataset.price (always the unit price, needed as-is for #updateTotals' own math).
   * @param {HTMLElement} card
   */
  #updateCardPriceDisplay(card) {
    const priceEl = card.querySelector('.bundle-recipe-card__price');
    if (!priceEl) return;

    const unitPrice = Number(card.dataset.price) || 0;
    const quantity = this.#cardQuantity(card);
    priceEl.replaceChildren(this.#buildSupermarketPrice(unitPrice * quantity));
  }

  addToCart() {
    const pakketId = `pkt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    /** @type {Array<{id: number, quantity: number, properties: Record<string, string>}>} */
    const items = [];

    for (const card of this.#cards) {
      if (card.dataset.included !== 'true') continue;

      const quantity = this.#cardQuantity(card);
      if (quantity <= 0) continue;

      items.push({
        id: Number(card.dataset.variantId),
        quantity,
        properties: {
          'Afhalen bij': card.dataset.vendor || '',
          Pakket: this.dataset.productTitle || '',
          _pakket_id: pakketId,
          _pakket_rol: card.dataset.group || '',
        },
      });
    }

    if (items.length === 0) return;

    if (this.refs.submitButton) this.refs.submitButton.disabled = true;
    if (this.refs.error) {
      this.refs.error.hidden = true;
      this.refs.error.textContent = '';
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];
    cartItemsComponents.forEach((node) => {
      if (node instanceof HTMLElement && node.dataset.sectionId) sectionIds.push(node.dataset.sectionId);
    });

    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferredUpdatePromise.promise,
      })
    );

    fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      })
    )
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent({
              error: response.message || 'Add to cart failed',
              code: 'INVALID',
              detail: {
                description: response.description,
                errors: response.errors,
              },
            })
          );
          if (this.refs.error) {
            this.refs.error.hidden = false;
            this.refs.error.textContent = response.message;
          }

          const ajaxCart = await fetch('/cart.js').then((r) => r.json());
          deferredUpdatePromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
            detail: { didError: true, source: 'bundle-recipe-builder', itemCount },
          });
          return;
        }

        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        this.#animateAdded();

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            source: 'bundle-recipe-builder',
            itemCount,
            sections: response.sections,
            didError: false,
          },
        });
      })
      .catch((error) => {
        console.error(error);
        deferredUpdatePromise.reject(error);
      })
      .finally(() => {
        this.#updateTotals();
      });
  }

  /** @returns {HTMLElement[]} */
  get #cards() {
    return Array.from(this.querySelectorAll('.bundle-recipe-card'));
  }

  /**
   * Only touches the shared stepper's public API — never the card's own bookkeeping, which
   * #handleStepperChange updates from the resulting event once the stepper has actually
   * applied the change.
   * @param {number} value
   */
  #setPlankQuantity(value) {
    this.#plankQuantity = Math.min(MAX_PLANKEN, Math.max(1, value));

    if (this.refs.plankValue) {
      const { unit = '', unitPlural } = this.refs.plankValue.dataset;
      const label = this.#plankQuantity === 1 ? unit : unitPlural || unit;
      this.refs.plankValue.textContent = `${this.#plankQuantity} ${label}`;
    }
    if (this.refs.plankMinus) this.refs.plankMinus.disabled = this.#plankQuantity <= 1;
    if (this.refs.plankPlus) this.refs.plankPlus.disabled = this.#plankQuantity >= MAX_PLANKEN;

    for (const card of this.#cards) {
      if (card.dataset.group !== 'hoofditems') continue;

      const stepper = this.#stepperFor(card);
      if (stepper && typeof (/** @type {any} */ (stepper).setQuantity) === 'function') {
        /** @type {any} */ (stepper).setQuantity(this.#plankQuantity);
      }
    }
  }

  /**
   * Zelfde "toegevoegd"-animatie (checkmark-burst) als de standaard add-to-cart-knop
   * (assets/product-form.js AddToCartComponent#animateAddToCart) — puur CSS-gedreven via
   * [data-added='true'], zie assets/base.css. Geen <add-to-cart-component>/<form> hier (dit is
   * geen single-variant productformulier maar een custom multi-item toevoeging), dus de losse
   * class in plaats van het hele component hergebruiken.
   */
  #animateAdded() {
    const { submitButton } = this.refs;
    if (!submitButton) return;

    clearTimeout(this.#addedTimeout);
    submitButton.dataset.added = 'true';
    this.#addedTimeout = window.setTimeout(() => {
      delete submitButton.dataset.added;
    }, 800);
  }

  /** @param {HTMLElement} card */
  #cardQuantity(card) {
    return Number(card.dataset.quantity) || 0;
  }

  #updateTotals() {
    if (!this.refs.submitButton || !this.refs.footerPrice) return;

    let hoofditemTotal = 0;
    let otherTotal = 0;
    let hasIncludedHoofditem = false;
    let itemCount = 0;

    for (const card of this.#cards) {
      if (card.dataset.included !== 'true') continue;

      const quantity = this.#cardQuantity(card);
      if (quantity <= 0) continue;

      const price = Number(card.dataset.price) || 0;
      itemCount += quantity;

      if (card.dataset.group === 'hoofditems') {
        hoofditemTotal += price * quantity;
        hasIncludedHoofditem = true;
      } else {
        otherTotal += price * quantity;
      }
    }

    const discountCents = Number(this.dataset.discountCents) || 0;
    const appliedDiscount = hasIncludedHoofditem ? Math.min(discountCents, hoofditemTotal) : 0;
    const total = hoofditemTotal - appliedDiscount + otherTotal;
    const originalTotal = hoofditemTotal + otherTotal;

    this.refs.footerPrice.replaceChildren(this.#buildSupermarketPrice(total));

    if (this.refs.footerOriginalPrice) {
      if (appliedDiscount > 0) {
        this.refs.footerOriginalPrice.textContent = this.#formatMoney(originalTotal);
        this.refs.footerOriginalPrice.hidden = false;
      } else {
        this.refs.footerOriginalPrice.hidden = true;
      }
    }

    this.refs.submitButton.disabled = itemCount === 0;

    this.#syncPdpHeadlinePrice(total);
  }

  /**
   * Mirrors the live bundle total onto the PDP's own headline price block (blocks/price.liquid,
   * rendered as `<product-price data-product-id="...">`) so the price near the title — not just
   * this block's own sticky footer — reflects the current selection. Matched purely by product
   * id: this block and that one are otherwise unrelated.
   * @param {number} totalCents
   */
  #syncPdpHeadlinePrice(totalCents) {
    const productId = this.dataset.productId;
    if (!productId) return;

    const priceBlocks = document.querySelectorAll(`product-price[data-product-id="${productId}"]`);
    for (const block of priceBlocks) {
      // Regular price: the bare `.price` directly in the container (the sale group also carries
      // `.price` but wraps the visually-hidden label, so it is excluded via `.price-item__group`).
      const priceEls = block.querySelectorAll('.price-container > .price:not(.price-item__group), .price-item--sale.price');
      for (const el of priceEls) el.replaceChildren(this.#buildSupermarketPrice(totalCents));
    }
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    return `€ ${(cents / 100).toFixed(2).replace('.', ',')}`;
  }

  /**
   * Builds the same markup as snippets/supermarket-price.liquid (big euros, small superscript
   * cents) so the live total matches every card's server-rendered price styling. Its `.prijs`
   * CSS is already loaded (that snippet is rendered on every card), so no duplicate CSS here.
   * @param {number} cents
   * @returns {HTMLElement}
   */
  #buildSupermarketPrice(cents) {
    const euros = Math.floor(cents / 100);
    const centsPart = String(cents % 100).padStart(2, '0');

    const wrapper = document.createElement('span');
    wrapper.className = 'prijs';

    const euroEl = document.createElement('span');
    euroEl.className = 'prijs__euro';
    euroEl.textContent = String(euros);

    const separator = document.createElement('span');
    separator.className = 'prijs__separator';
    separator.textContent = '.';

    const centEl = document.createElement('span');
    centEl.className = 'prijs__cent';
    centEl.textContent = centsPart;

    wrapper.append(euroEl, separator, centEl);
    return wrapper;
  }
}

if (!customElements.get('bundle-recipe-builder-component')) {
  customElements.define('bundle-recipe-builder-component', BundleRecipeBuilder);
}
